import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Reveal, Kick } from "../components/Shared";
import Seo from "../components/Seo";
import NotFound from "./NotFound";
import { subscribe } from "../lib/api";
import { getCollectionPiece, collectionSizes, framedUpcharge, collectionBundle } from "../data/collection";

const VIEWS = [
  { key: "art", label: "Artwork" },
  { key: "poster", label: "With type" },
  { key: "gym", label: "In the gym" },
  { key: "office", label: "In the office" },
];

export default function CollectionPiece() {
  const { slug } = useParams();
  const piece = getCollectionPiece(slug);
  const [view, setView] = useState("art");
  const [size, setSize] = useState(() =>
    Math.max(0, collectionSizes.findIndex((s) => s.label === piece?.heroSize))
  );
  const [finish, setFinish] = useState("canvas");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  if (!piece) return <NotFound />;

  const price = collectionSizes[size].price + (finish === "framed" ? framedUpcharge : 0);
  const comp = piece.complementary.map(getCollectionPiece);
  const landscape = view === "gym" || view === "office";

  const reserve = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await subscribe(email, `waitlist:collection-${piece.slug}`, piece.title);
      toast.success(res.duplicate ? "You already reserved this piece." : "Reserved. You get first claim when the collection drops.");
      setEmail("");
    } catch {
      toast.error("Something broke. Try again.");
    }
    setBusy(false);
  };

  return (
    <>
      <Seo title={piece.seoTitle} description={piece.seoDescription} path={`/collection/${piece.slug}`} noindex />

      <section className="coll">
        <div className="wrap">
          <p className="crumbs" data-testid="piece-breadcrumbs">
            <Link to="/collection">Collection 001</Link> <span>/</span> No. {piece.num}
          </p>

          <div className="pgrid">
            <div>
              <div className={`pview ${landscape ? "wide" : ""}`} data-testid="piece-main-view">
                <img src={piece.images[view]} alt={view === "art" ? piece.altArt : `${piece.title} — ${view} view`} />
              </div>
              <div className="pthumbs">
                {VIEWS.map((v) => (
                  <button
                    key={v.key}
                    className={`pthumb ${view === v.key ? "active" : ""}`}
                    onClick={() => setView(v.key)}
                    data-testid={`piece-view-${v.key}`}
                  >
                    <img src={piece.images[v.key]} alt={v.label} loading="lazy" />
                    <span>{v.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="pinfo">
              <Kick k="幽">Collection 001 · No. {piece.num}</Kick>
              <h1 className="coll-h1" style={{ fontSize: "clamp(34px,4.4vw,54px)" }}>{piece.title}</h1>
              <p className="pmsg" data-testid="piece-message">{piece.message}</p>

              <div className="insightbox">
                <p className="label mut">The principle</p>
                <p>{piece.insight}</p>
              </div>

              <p className="label mut" style={{ marginTop: 26 }}>Size · hero size {piece.heroSize}</p>
              <div className="sizegrid5">
                {collectionSizes.map((s, i) => (
                  <button
                    key={s.label}
                    className={`sizebtn5 ${size === i ? "active" : ""}`}
                    onClick={() => setSize(i)}
                    data-testid={`piece-size-${i}`}
                  >
                    <strong>{s.label}</strong>
                    <span>${s.price + (finish === "framed" ? framedUpcharge : 0)}</span>
                  </button>
                ))}
              </div>

              <p className="label mut" style={{ marginTop: 22 }}>Finish</p>
              <div className="finishrow">
                <button
                  className={`finishbtn ${finish === "canvas" ? "active" : ""}`}
                  onClick={() => setFinish("canvas")}
                  data-testid="piece-finish-canvas"
                >
                  Gallery canvas
                </button>
                <button
                  className={`finishbtn ${finish === "framed" ? "active" : ""}`}
                  onClick={() => setFinish("framed")}
                  data-testid="piece-finish-framed"
                >
                  Framed canvas +${framedUpcharge}
                </button>
              </div>

              <div className="pprice" data-testid="piece-price">${price}</div>

              <form className="nform" onSubmit={reserve} aria-label="Reserve this piece">
                <input
                  type="email"
                  required
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="piece-reserve-email"
                />
                <button className="cutbtn" type="submit" disabled={busy} data-testid="piece-reserve-submit">
                  {busy ? "Reserving…" : "Reserve the piece"}
                </button>
              </form>
              <p className="finemut">Prototype preview. Reserving costs nothing and holds first claim for the production run.</p>

              <ul className="speclist" data-testid="piece-spec-list">
                <li>Museum-grade matte canvas, archival pigment inks</li>
                <li>Gallery wrap or matte black float frame</li>
                <li>Single vermilion accent, print-checked blacks</li>
                <li>Art-only and typographic poster versions available</li>
                <li>Best in: {piece.environment}</li>
              </ul>
            </div>
          </div>

          {/* story */}
          <Reveal className="pstory" as="section">
            <Kick k="話">The story</Kick>
            {piece.story.map((s, i) => (
              <p key={i}>{s}</p>
            ))}
          </Reveal>

          {/* scale */}
          <Reveal className="pscale" as="section">
            <p className="label mut">True scale · center hung at 58 in</p>
            <img src="/collection/shared/scale-reference.webp" alt="All five print sizes shown to scale beside a six-foot figure" loading="lazy" data-testid="piece-scale-image" />
          </Reveal>

          {/* complementary */}
          <section className="pcomp">
            <Reveal>
              <Kick k="対">Completes the wall</Kick>
              <h2 className="sec" style={{ fontSize: "clamp(26px,3.6vw,40px)" }}>Pairs with No. {piece.num}.</h2>
            </Reveal>
            <div className="compgrid">
              {comp.map((c) => (
                <Reveal key={c.slug} className="compcard" as="article">
                  <Link to={`/collection/${c.slug}`} data-testid={`complementary-${c.slug}`}>
                    <img src={c.images.thumb} alt={c.altArt} loading="lazy" />
                    <div>
                      <p className="label mut">No. {c.num}</p>
                      <h3>{c.title}</h3>
                      <p className="cline">{c.message}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
              <Reveal className="compcard bundlecard" as="article">
                <div>
                  <p className="label mut">{collectionBundle.size}</p>
                  <h3>{collectionBundle.title}</h3>
                  <p className="cline">{collectionBundle.line}</p>
                  <p className="bundle-price" style={{ marginTop: 10 }}>
                    <strong>${collectionBundle.price}</strong> <s>${collectionBundle.compare}</s>
                  </p>
                  <Link className="viewcta" to="/collection" data-testid="bundle-back-link">
                    See the set <i>—</i>
                  </Link>
                </div>
              </Reveal>
            </div>
          </section>
        </div>
      </section>
    </>
  );
}
